import React, { useState } from "react";
import { ClaimWithDetails, CommunicationLog } from "@/types";
import { formatDate, formatCurrency } from "@/lib/utils";
import { useTenantCurrency } from "@/hooks/use-tenant";
import { formatCurrencyWithTenantAndConversion } from "@/lib/currency-conversion";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  ClipboardList, 
  FileText, 
  MessageSquare, 
  History, 
  Clock
} from "lucide-react";
import DocumentList from "@/components/documents/document-list";
import ClaimStageWorkflow from "@/components/claims/claim-stage-workflows";
import ClaimHistory from "@/components/claims/claim-history";
import { ClaimStatus } from "@/lib/claim-status-constants";

// Navigation context types (matching claim-detail-page.tsx)
type ViewMode = 'active' | 'reviewing';

interface NavigationContext {
  viewMode: ViewMode;
  reviewingStage: ClaimStatus | null;
  activeStage: ClaimStatus;
}

interface ClaimDetailTabsProps {
  claimData: ClaimWithDetails;
  navigationContext: NavigationContext;
  currentViewingStage: ClaimStatus | null;
  onOpenEmailDialog: () => void;
  onClaimUpdate: () => void;
  onReturnToActiveStage: () => void;
}

export default function ClaimDetailTabs({
  claimData,
  navigationContext,
  currentViewingStage,
  onOpenEmailDialog,
  onClaimUpdate,
  onReturnToActiveStage,
}: ClaimDetailTabsProps) {
  const [currentTab, setCurrentTab] = useState("workflow");
  const { currency: tenantCurrency } = useTenantCurrency();
  const claim = claimData.claim;
  const documents = claimData.documents;
  const communicationLogs = claimData.communicationLogs;

  return (
    <Tabs value={currentTab} onValueChange={setCurrentTab}>
      <TabsList className="w-full justify-start">
        <TabsTrigger value="workflow" className="flex items-center">
          <Clock className="h-4 w-4 mr-2" />
          Current Stage
        </TabsTrigger>
        <TabsTrigger value="details" className="flex items-center">
          <ClipboardList className="h-4 w-4 mr-2" />
          Details
        </TabsTrigger>
        <TabsTrigger value="documents" className="flex items-center">
          <FileText className="h-4 w-4 mr-2" />
          Documents
        </TabsTrigger>
        <TabsTrigger value="communication" className="flex items-center">
          <MessageSquare className="h-4 w-4 mr-2" />
          Communication
        </TabsTrigger>
        <TabsTrigger value="history" className="flex items-center">
          <History className="h-4 w-4 mr-2" />
          History
        </TabsTrigger>
      </TabsList>

      {/* Details Tab */}
      <TabsContent value="details" className="py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Claim Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Claim Amount</label>
                    <p className="text-lg font-semibold">
                      {formatCurrencyWithTenantAndConversion(claim.claimAmount, claim.claimAmountCurrency, tenantCurrency, {
                        showSymbol: true,
                        showOriginal: false,
                        showConversionRate: false
                      })}
                    </p>
                    {claim.claimAmountCurrency !== tenantCurrency && (
                      <p className="text-xs text-blue-600 mt-1">
                        💱 Converted from {claim.claimAmountCurrency}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    <p className="text-lg font-semibold">{claim.status}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Type</label>
                    <p className="text-lg">{claim.claimType || "Not specified"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Date Created</label>
                    <p className="text-lg">{formatDate(claim.createdAt)}</p>
                  </div>
                </div>
                
                {/* Claim headline */}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Headline</label>
                  <p className="whitespace-pre-line">{claim.claimHeadline}</p>
                </div>
                
                {/* Claim summary */}
                {claim.claimSummary && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Summary</label>
                    <p className="whitespace-pre-line">{claim.claimSummary}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Total Documents</label>
                  <p className="text-2xl font-bold">{documents.length}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Communications</label>
                  <p className="text-2xl font-bold">{communicationLogs.length}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </TabsContent>

      {/* Documents Tab */}
      <TabsContent value="documents" className="py-6">
        <div className="space-y-6">
          <h3 className="text-lg font-medium">Claim Documents</h3>
          <DocumentList 
            documents={claimData.documents.map(doc => ({
              documentMetadataId: doc.document.documentMetadataId,
              tenantId: doc.document.tenantId,
              fileName: doc.document.fileName,
              createdAt: doc.document.createdAt,
              documentType: doc.claimDocument.documentType
            }))} 
            entityId={claim.claimId} 
            entityType="claim" 
            hideUploadButton={false}
          />
        </div>
      </TabsContent>

      {/* Communication Tab */}
      <TabsContent value="communication" className="py-6">
        <div className="space-y-6">
          <h3 className="text-lg font-medium">Communication History</h3>
          
          {communicationLogs && communicationLogs.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <div className="divide-y">
                {communicationLogs
                  .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                  .map((log: CommunicationLog) => (
                    <div key={log.logId} className="p-4 hover:bg-muted/10">
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <div className="flex items-center">
                            <span 
                              className={`w-2 h-2 rounded-full mr-2 ${
                                log.logType === 'Email Sent' ? 'bg-blue-500' : 
                                log.logType === 'Email Received' ? 'bg-green-500' : 
                                log.logType === 'Call' ? 'bg-amber-500' : 
                                'bg-neutral-500'
                              }`} 
                            />
                            <span className="font-medium">
                              {log.subject || log.logType}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {log.logType} by {log.userId.substring(0, 8)}
                          </p>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {formatDate(log.timestamp)}
                        </div>
                      </div>
                      <div className="mt-2 bg-muted/10 p-3 rounded whitespace-pre-line text-sm">
                        {log.body}
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          ) : (
            <div className="text-center py-8 border rounded-lg">
              <p className="text-muted-foreground">No communication logs found for this claim.</p>
            </div>
          )}
        </div>
      </TabsContent>

      {/* Current Status Workflow Tab */}
      <TabsContent value="workflow" className="py-6">
        <ClaimStageWorkflow 
          claimData={claimData}
          navigationContext={navigationContext}
          currentViewingStage={currentViewingStage}
          onOpenEmailDialog={onOpenEmailDialog}
          onClaimUpdate={onClaimUpdate}
          onReturnToActiveStage={onReturnToActiveStage}
        />
        {/* Note: Component name should be updated to ClaimStatusWorkflow in a future task */}
      </TabsContent>
      
      {/* History Tab */}
      <TabsContent value="history" className="py-6">
        <ClaimHistory claimData={claimData} />
      </TabsContent>
    </Tabs>
  );
}